import React from "react";
import { Route } from "react-router-dom";
import { UserRoleEnum } from "@/types/role.type";
import type { RouteCtx } from "../routeContext";
import { PlaceholderPage, NotFound } from "../shell";
import {
  StudyCoordinatorDashboard,
  SiteManagement,
  CalendarPage,
  RegulatoryApprovalsMain,
  ApprovalDetailPage,
  SubjectsManagement,
  VisitDataEntry,
  DataCompletenessDashboard,
  DocumentLibrary,
  UploadDocument,
  ETMFCompletenessDashboard,
  MyTasksCTMS,
  IssueRiskTrackerPage,
  CDAManagement,
  RBQMCenter,
} from "../lazyPages";

/** studyCoordinatorRoutes — order preserved for React Router first-match. */
export function studyCoordinatorRoutes(ctx: RouteCtx): React.ReactNode {
  const {
    userRole,
    selectedStudyId,
    availableStudies,
    hasRbqmAccess,
    currentStudy,
    onLogout,
  } = ctx;

  if (userRole !== UserRoleEnum.STUDY_COORDINATOR) {
    return null;
  }

  return (
    <>
      <Route
        path="/dashboard/study-coordinator"
        element={
          <StudyCoordinatorDashboard onLogout={onLogout} userRole={userRole} />
        }
      />
      <Route
        path="/sc/study-overview"
        element={<PlaceholderPage title="Study Overview" />}
      />
      <Route
        path="/sc/site-management"
        element={
          <SiteManagement
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            availableStudies={availableStudies}
          />
        }
      />
      <Route
        path="/sc/tasks"
        element={
          <MyTasksCTMS
            userRole={userRole}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/sc/my-activity-log"
        element={<PlaceholderPage title="My Activity Log" />}
      />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/subjects" element={<PlaceholderPage title="Subjects" />} />
      <Route
        path="/scheduling"
        element={<PlaceholderPage title="Visit Scheduling" />}
      />
      <Route path="/ecrfs" element={<PlaceholderPage title="eCRFs" />} />
      <Route path="/queries" element={<PlaceholderPage title="Queries" />} />
      <Route
        path="/documents"
        element={<PlaceholderPage title="Documents" />}
      />

      {/* EDC Routes */}
      <Route path="/edc/subjects" element={<SubjectsManagement />} />
      <Route
        path="/edc/visit-data-entry"
        element={<VisitDataEntry userRole={userRole} />}
      />
      <Route
        path="/edc/data-completeness"
        element={<DataCompletenessDashboard />}
      />

      {/* eTMF Routes */}
      <Route
        path="/etmf/document-library"
        element={
          <DocumentLibrary
            userRole={userRole}
            selectedStudyId={selectedStudyId}
          />
        }
      />
      <Route
        path="/etmf/upload-document"
        element={
          <UploadDocument
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/completeness-dashboard"
        element={
          <ETMFCompletenessDashboard selectedStudyId={selectedStudyId} />
        }
      />

      {/* IWRS Routes */}
      <Route
        path="/iwrs/randomization"
        element={<PlaceholderPage title="Randomization" />}
      />
      <Route
        path="/iwrs/kit-assignment"
        element={<PlaceholderPage title="Kit Assignment" />}
      />
      <Route
        path="/sc/regulatory"
        element={
          <RegulatoryApprovalsMain
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/sc/regulatory/:studyId/:id"
        element={<ApprovalDetailPage />}
      />
      <Route
        path="/sc/risk-tracker"
        element={
          <IssueRiskTrackerPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/sc/rbqm-center"
        element={
          hasRbqmAccess ? (
            <RBQMCenter userRole={userRole} currentStudy={currentStudy} />
          ) : (
            <NotFound />
          )
        }
      />
      <Route
        path="/sc/cda-management"
        element={
          <CDAManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
    </>
  );
}
